import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { DatePicker } from 'antd';
import { projectActions, projectEntryActions, projectEntrySelectors, projectSelectors, bulkEntryActions, engineeringEntryActions, labEntryActions, prototypeEntryActions } from '@/store';
import { getLrProjectsError, getLrProceduresError, projectEntriesError, StyledModalHeaderIcon } from '@/shared';
import { errorSavingEntry, errorSavingEntryHours } from '../../shared/constants/toastrErrorMsg';
import { Dropdown } from '../../components/Dropdown';
import { ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { StyledModal, StyledHoursInput, StyledAddTaskButton, StyledInlineErrorMessage } from './styled';
import { isArrayWithLength, isTimeEntryDateValid, isBeforeStartOfCurrentMonth, handleFetchError, handleFetchSuccess, emitToastrWarning } from '@/helpers';
import { Button, Checkbox } from 'antd';
import { toastr } from 'react-redux-toastr';
import ProcedureRadioGroup from '../Procedure/ProcedureRadioGroup.jsx';
import { SupportedSystems } from '@/shared';
import { Grid } from '@mantine/core';
import '@/styles/app.css';
import dayjs from 'dayjs';

const BulkTimeEntry = () => {
  const dispatch = useDispatch();

  const showBulkEntryModal = useSelector(state => state.bulkEntry.showBulkEntryModal);


  const oid = useSelector(state => state.user.activeUser?.oid);
  const type = useSelector(projectEntrySelectors.selectCurrentSystem);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const projects = useSelector(projectSelectors.projectsForCurrentSystem);
  const procedures = useSelector(state => state.projects.lrs.procedures);
  const projectsAreFetched = isArrayWithLength(projectSelectors.projectsForCurrentSystem);
  const NumberOfPreviousTimeEntryMonthsAllowed = useSelector(state => state.config.configuration.previousMonthBack);
  const isLoading = useSelector(state => state.bulkEntry.isLoading);

  const timeBucketCategories = useSelector(state => state.timeBucket.timeBucketCategories.data);

  const removeWeekendsRef = useRef();
  const { RangePicker } = DatePicker;

  const [hoursInputVal, setHoursInputVal] = useState('');
  const [removeWeekends, setRemoveWeekends] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inlineErrorMessage, setInlineErrorMessage] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  const isProceduresValid = (type === SupportedSystems.LRS && selectedOption && selectedOption.type !== SupportedSystems.ADMIN);

  // Invalid field indicators
  const [projectInvalid, setProjectInvalid] = useState(false);
  const [hoursInputInvalid, setHoursInputInvalid] = useState(false);
  const [dateInputInvalid, setDateInputInvalid] = useState(false);

  const getDefaultDateRange = () => {
    let mStartDate = moment(selectedDays[0]);
    let mEndDate = moment(selectedDays[6]);

    if (isBeforeStartOfCurrentMonth(mStartDate)) {
      mStartDate = moment().startOf('month');
    }
    if (isBeforeStartOfCurrentMonth(mEndDate)) {
      mEndDate = moment().startOf('month');
    }

    return {
      startDate: mStartDate,
      endDate: mEndDate,
    }
  }

  // Initialize start and end date parameters for datepicker (if dates are not in current month and greater - set range based on current date)
  const [dateRange, setDateRange] = useState(() => {
    return getDefaultDateRange();
  });

  const fixedOptions = useMemo(() => {
    return timeBucketCategories.reduce((acc, entry) => {
      const options = (entry.name === 'Administrator' && isArrayWithLength(entry.timeBuckets)) ? 
        entry.timeBuckets.map(q => (
          {
            ...q,
            type: SupportedSystems.ADMIN, 
            timeBucketId: q.id,
          })) 
        : [];
      return acc.concat(options);
    }, []);
  }, [timeBucketCategories]);

  useEffect(() => {
    removeWeekendsRef.current = document.getElementById('remove_weekend_hours');
  }, [timeBucketCategories]);

  useEffect(() => {
    if (!projectsAreFetched) {
      if (type === SupportedSystems.LRS) {
        dispatch(projectActions.getAllLrsProjects()).catch(e => handleFetchError(e, getLrProjectsError));
      } else if (type === SupportedSystems.VPIN) {
        dispatch(projectActions.getAllVpinProjects(dateRange.startDate, dateRange.endDate)).catch(e => handleFetchError(e, getLrProjectsError));
      }
    }
  }, [projectsAreFetched, type, dateRange])

  useEffect(() => {
    if (isProceduresValid && selectedOption && selectedOption.requestNumber && !procedures[selectedOption.requestNumber]) {
      dispatch(projectActions.getLrsProcedures(selectedOption.requestNumber, selectedOption.isNewLR)).catch((e) => handleFetchError(e, getLrProceduresError));
    }
  }, [selectedOption, isProceduresValid, procedures]);

  const proceduresForCurrentProject = useMemo(() => {
    if (!selectedOption || !selectedOption.requestNumber || !procedures[selectedOption.requestNumber]) {
      return {
        data: []
      };
    }
    return procedures[selectedOption.requestNumber];
  }, [selectedOption, procedures]);

  const sortedProceduresForCurrentProject = useMemo(() => {
    const { data, ...rest } = proceduresForCurrentProject;
    return {
      ...rest,
      data: proceduresForCurrentProject.data.sort((a, b) => a.procedureNumber - b.procedureNumber) || [],
    }
  }, [proceduresForCurrentProject]);

  const onSetDateRange = useCallback((dateString) => {
    const startDate = moment(dateString[0]);
    const endDate = moment(dateString[1]);

    if (isTimeEntryDateInvalid(startDate)) {
      setDateInputInvalid(true)
      return
    }

    const monthDiff = endDate ? endDate.diff(startDate, 'month') : 0;
    if (monthDiff >= 3) {
      setDateInputInvalid(true);
      const defaultDateRange = getDefaultDateRange();
      setDateRange(defaultDateRange);
    } else {
      setDateInputInvalid(false);
      setDateRange({ startDate, endDate });
    }
  }, []);

  const onOptionChange = useCallback((option) => {
    setSelectedOption(option);
  });

  const closeBulkEntryModal = () => {
    setTimeout(() => {
      dispatch(bulkEntryActions.setShowBulkEntryModal(false));
    }, 300);
  };

  const resetDataFields = () => {
    setHoursInputVal('');
    setSelectedOption(null);
    setInlineErrorMessage(null);

    // Reset removeweekends checkbox to default value
    if (!removeWeekends && removeWeekendsRef.current) {
      removeWeekendsRef.current.click();
    }
  };
  
  const buildV2EntryBody = () => {
    if (selectedOption.type === SupportedSystems.VPIN) {
      return {
        startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
        userId: oid,
        hours: hoursInputVal,
        projectId: selectedOption.projectId,
        projectNumber: selectedOption.projectNumber,
        timezone: new Date().getTimezoneOffset(),
        excludeWeekends: removeWeekends,
      }
    }
    
    if (selectedOption.type === SupportedSystems.LRS) {
      return {
        startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
        userId: oid,
        hours: hoursInputVal,
        requestNumber: selectedOption.requestNumber,
        requestId: selectedOption.requestId,
        projectNumber: selectedOption.projectNumber,
        timezone: new Date().getTimezoneOffset(),
        procedureId: selectedProcedure?.procedureIdLegacy,
        excludeWeekends: removeWeekends,
        isNewLR: selectedOption.isNewLR,
        procedure: {
          Id: selectedProcedure.id,
          ProcedureIdLegacy: selectedProcedure.procedureIdLegacy,
          ProcedureNumber: selectedProcedure.procedureNumber,
          ProcedureAlphaId: selectedProcedure.procedureAlphaId,
          ProcedureTitle: selectedProcedure.procedureTitle,
          ProcedureExplanation: selectedProcedure.procedureExplanation,
          Type: selectedProcedure.type,
          Oid: selectedProcedure.oid,
          SystemId: selectedProcedure.systemId,
        }
      }
    }

    if (selectedOption.type === SupportedSystems.ADMIN) {
      return {
        startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
        endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
        userId: oid,
        hours: hoursInputVal,
        TimeBucketId: selectedOption.timeBucketId,
        timezone: new Date().getTimezoneOffset(),
        excludeWeekends: removeWeekends,
      }
    }
  }

  const handleOnHoursInputBlur = (e) => {
    const numValue = Number(e.target.value);
    if (isNaN(numValue) || numValue < 0 || numValue > 12) {
      const warnMsg = isNaN(numValue)
        ? 'Please enter a number from 1-12 that is divisible by 0.5.'
        : (numValue < 0)
          ? `${numValue} is less than the minimum allowed of 0.`
          : `${numValue} is greater than the maximum allowed of 12.`;

      emitToastrWarning('Hours', warnMsg);
      setHoursInputInvalid(true);
      setHoursInputVal('');
      return;
    }

    const strValue = numValue.toString();
    const roundedValue = (strValue.indexOf('.') > -1 && Number(strValue.split('.')[1]) > 0)
      ? Math.min(Number(`${strValue.split('.')[0]}.5`), 12)
      : numValue;

    setHoursInputInvalid(false);
    setHoursInputVal(roundedValue.toString());
  };

  const handleBulkEntryPostProcess = async (oid, closeModalOnComplete, successful = true) => {
    const startDate = moment(dateRange.startDate).format('YYYY-MM-DD');
    const endDate = moment(dateRange.endDate).format('YYYY-MM-DD');

    // since admin is a child of the other systems it gets refreshed without having to specify it
    if(successful){
      switch (type) {
        case SupportedSystems.VPIN:
          await dispatch(engineeringEntryActions.getEngineeringProjectTimeEntries(oid, startDate, endDate))
          break;
        case SupportedSystems.LRS:
          await dispatch(labEntryActions.getLabTimeEntries(oid, startDate, endDate));
          break;
        case SupportedSystems.GPRS:
          await dispatch(prototypeEntryActions.getPrototypeTimeEntries(oid, startDate, endDate));
          break;
        default:
          return;
      }
    }

    if (closeModalOnComplete) {
      closeBulkEntryModal();
    } else if (successful) {
      resetDataFields();
    }
  };

  const resetValidationState = () => {
    setHoursInputInvalid(false);
    setProjectInvalid(false);
    setDateInputInvalid(false);
  }

  const isBulkEntryValid = () => {
    if (isLoading) {
      return false;
    }
    toastr.removeByType('warning');
    const isHoursInvalid = !hoursInputVal || Number(hoursInputVal) <= 0;

    if (isHoursInvalid || !selectedOption || !dateRange?.startDate.isValid() || !dateRange?.endDate.isValid()) { // NO PROJECT, HOURS, OR DATE RANGE VALUES
      !selectedOption && setProjectInvalid(true);
      (!dateRange?.startDate.isValid() || !dateRange?.endDate.isValid()) && setDateInputInvalid(true);
      
      if (isHoursInvalid) {
        setHoursInputInvalid(true);
        emitToastrWarning('Invalid Hours', 'Hours must be greater than 0 and in half hour increments.');
        return false;
      }

      emitToastrWarning('Missing Fields', 'Please fill in all required fields before proceeding.');
      return false;
    } else if (isProceduresValid && (selectedOption && !selectedProcedure)) { // NO PROCEDURE SELECTED (DOES NOT APPLY FOR VPIN)
      resetValidationState();
      emitToastrWarning('No Procededure', `Please select a procedure for LRS-${selectedOption.requestNumber}`);
      return false;
    }
    return true;
  }

  const handlePostBulkEntry = async (closeModalOnComplete = false) => {
    if(!isBulkEntryValid()) {
      return;
    }

    resetValidationState();

    try {
      const body = buildV2EntryBody();
      await dispatch(bulkEntryActions.addBulkTimeEntry(body, selectedOption.type))
      dispatch(projectEntryActions.requestMonthlyCompletion(oid, moment(selectedDays[0]).format(), 0));
      try {
        handleBulkEntryPostProcess(oid, closeModalOnComplete);
        setTimeout(() => { handleFetchSuccess('Bulk Time Entry'); }, closeModalOnComplete ? 325 : 50);
      } catch (e) {
        handleFetchError(e, projectEntriesError);
        handleBulkEntryPostProcess(oid, closeModalOnComplete, false);
      }
    } catch (e) {
      handleFetchError(e, getErrMsg(e));
      handleBulkEntryPostProcess(oid, true, false);
    }
  };

  const getErrMsg = (e) => {
    // If error has a Message property (from ValidationErrorResponse), use it directly
    if (e && e.Message) {
      return { title: 'Error: Project Entries', message: e.Message };
    }
    if (e.Message === errorSavingEntryHours.message) {
      return errorSavingEntryHours;
    }
    return errorSavingEntry;
  }

  const isTimeEntryDateInvalid = (day) => {
    return !isTimeEntryDateValid(day, NumberOfPreviousTimeEntryMonthsAllowed);
  }

  const handleChange = (e) => {
    setRemoveWeekends(e.target.checked);
  };

  return (
    <StyledModal
      isOpen={showBulkEntryModal}
      size='md'
      centered
    >
      <ModalHeader
        toggle={closeBulkEntryModal}
        disabled={isLoading}
      >
        <StyledModalHeaderIcon icon="clock" />Bulk Time Entry
      </ModalHeader>
      <ModalBody>
        <Grid>
          <Grid.Col
            invalid={dateInputInvalid}
          >
            <RangePicker
              status={dateInputInvalid ? 'error' : ''}
              onChange={(_, dateString) => (onSetDateRange(dateString))}
              value={[dayjs(dateRange.startDate), dayjs(dateRange.endDate)]}
              allowClear={false}
              format={'MM-DD-YYYY'}
            />
          </Grid.Col>
        </Grid>
        <p style={{ marginLeft: '1px', marginTop: '4px' }}>
          Please only enter 3 months at a time.
        </p>
        <Grid style={{ paddingLeft: '1px', paddingTop: '4px' }}>
          <Grid.Col>
            <Checkbox
              id={'remove_weekend_hours'}
              checked={removeWeekends}
              onChange={handleChange}
            >
              Exclude weekends
            </Checkbox>
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col>
            <Dropdown
              options={projects}
              fixedOptions={fixedOptions}
              value={selectedOption}
              placeholder='Search...'
              onChange={onOptionChange}
              invalid={projectInvalid}
              windowMode={true}
              dataTestId="bulk"
              getOptionValue={option => type === SupportedSystems.LRS ? option.requestNumber : option.projectNumber}
              getOptionLabel={option => (
                type === SupportedSystems.LRS
                  ? option.isNewLR
                    ? `GRS LR-${option.requestNumber}`
                    : `LR-${option.requestNumber}`
                  : `${option.projectNumber}`
              ).trim()}
              getFixedOptionValue={option => option.id}
              getFixedOptionLabel={option => option.name}
            />
          </Grid.Col>
        </Grid>
        {
          isProceduresValid && (
            <ProcedureRadioGroup
              project={selectedOption}
              procedures={sortedProceduresForCurrentProject}
              onSelectProcedure={setSelectedProcedure}
            />
          )
        }
        <Grid>
          <Grid.Col>
            <StyledHoursInput
              type='text'
              inputMode='decimal'
              data-test='bulk-hours-input'
              value={hoursInputVal}
              invalid={hoursInputInvalid}
              onBlur={handleOnHoursInputBlur}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setHoursInputVal(e.target.value)}
            />
            <span style={{ paddingLeft: '0.5rem' }}>Hours per day</span>
          </Grid.Col>
        </Grid>
        <Grid style={{ marginTop: '1rem' }}>
          <Grid.Col>
            <StyledAddTaskButton
              light
              disabled={isLoading}
              onClick={() => handlePostBulkEntry()}
            >
              Enter &amp; Add Another Task
            </StyledAddTaskButton>
          </Grid.Col>
        </Grid>
        {
          inlineErrorMessage && (
            <StyledInlineErrorMessage>{inlineErrorMessage}</StyledInlineErrorMessage>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button
          className="secondary-antd-button"
          primary
          outline
          onClick={closeBulkEntryModal}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          className="primary-antd-button"
          primary
          onClick={() => handlePostBulkEntry(true)}
          loading={isLoading}
        >
          Enter Time
        </Button>
      </ModalFooter>
    </StyledModal>
  );
};

export default BulkTimeEntry;